import Clarity from "@microsoft/clarity";
import LogRocket from "logrocket";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { RotateIcon, UpdateReloadIcon } from "./Assets/assets";
import Button from "./Components/Button/Button";
import SnackBar from "./Components/SnackBar/SnackBar";
import { useComplaints } from "./CustomHooks/useComplaint";
import useLiveLocation from "./CustomHooks/useLiveLocation";
import {
  forceRefreshAccessToken,
  logout
} from "./Redux/authentication/actions";
import { setFilter, setOfflineComplaints } from "./Redux/complaints/action";
import {
  addUserDistance,
  setSelectedLocation,
  setUserLocation
} from "./Redux/locations/action";
import Complaints from "./Screens/Complaints/Complaints";
import LocationSearch from "./Screens/LocationSearch/LocationSearch";
import Login from "./Screens/Login/Login";
import Profile from "./Screens/Profile/Profile";
import IeRaiseComplaint from "./Screens/RaiseComplaint/IeRaiseComplaint";
import SuccessFallback from "./Screens/RaiseComplaint/SuccessFallback";
import SupportRaiseComplaint from "./Screens/RaiseComplaint/SupportRaiseComplaint";
import SelectedComplaint from "./Screens/SelectedComplaint/SelectedComplaint";
import { useAuthSubscriber } from "./Services/authentication";
import {
  useCallsListener,
  useLocationListener,
  useProfileListener,
  PROFILE_SYNC_TIMEOUT_MS
} from "./Services/database";
import { isValidArray, isValidObject } from "./Services/validators";
import {
  defaultFilterData,
  generateFilterQuery,
  LocationFallBack,
  PermissionDeniedFallback
} from "./Utils/constants";

function App(props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [auth, setAuth] = useState({
    loggedIn: null,
    claims: null
  });
  const [formData, setFormData] = useState({
    name: "",
    issue: [],
    locationData: {},
    phoneNumber: "+91",
    callId: "",
    sourceType: "",
    period: "Hours",
    description: ""
  });
  const [locationAccess, setLocationAccess] = useState(null);

  useAuthSubscriber((props) => {
    setAuth({ claims: props.claims, loggedIn: props.isAuth });
  }, auth.loggedIn);

  const profileSyncState = useProfileListener({
    uid: props.auth.data.uid,
    phoneNumber: props.auth.data.phoneNumber,
    accessToken: props.auth.data.accessToken,
    isAuth: auth.loggedIn
  });

  useLocationListener({
    uid: props.auth?.data?.uid,
    isAuth: auth.loggedIn
  });

  useCallsListener({
    uid: props.auth?.data?.uid,
    phoneNumber: props.auth.data.phoneNumber,
    isAuth: auth.loggedIn && auth.claims?.moduleAccess?.support === true
  });

  const [getComplaintsNextPage, complaintsLoading] = useComplaints({
    filterConverter: (filterData) => {
      return generateFilterQuery(filterData, props.locations.data);
    },
    filterData: props.complaints?.filter,
    isAuth: auth.loggedIn && isValidArray(props.complaints?.filter?.status),
    claims: auth.claims,
    locationData: props.locations.data,
    phoneNumber: props.auth?.data?.phoneNumber,
    searchKey: props.complaints?.searchKey,
    sortBy: props.complaints?.sortBy,
    orderBy:
      auth.claims?.moduleAccess?.independentEngineer &&
      props.complaints?.filter?.status?.includes("PENDING CLOSURE")
        ? "status.updatedAt"
        : "createdAt"
  });
  //for snack bar messages
  useEffect(() => {
    if (props.status.message) {
      setStatus(props.status.message);
    } else {
      setStatus(null);
    }
  }, [props.status]);

  useEffect(() => {
    if (isValidObject(auth.claims)) {
      let data = defaultFilterData;

      if (auth.claims.moduleAccess?.independentEngineer) {
        data.status = ["PENDING CLOSURE"];
      } else {
        data.status = ["OPEN"];
      }
      props.setFilter(data);
    }
    // eslint-disable-next-line
  }, [auth.claims]);

  useEffect(() => {
    if (props.profile?.data?.accessSyncStatus === false && auth.loggedIn) {
      props.forceRefreshAccessToken(true);
    }
    // eslint-disable-next-line
  }, [props.profile?.data, auth.loggedIn]);

  //for log rocket init
  useEffect(() => {
    if (
      process.env.NODE_ENV === "production" &&
      window.location.hostname !== "localhost"
    ) {
      const release =
        typeof process.env.REACT_APP_BUILD_NUM === "string"
          ? { release: process.env.REACT_APP_BUILD_NUM }
          : {};
      if (process.env.REACT_APP_LOG_ROCKET_KEY) {
        LogRocket.init(process.env.REACT_APP_LOG_ROCKET_KEY, release);
      }

      if(process.env.REACT_APP_CLARITY_KEY){
        Clarity.init(process.env.REACT_APP_CLARITY_KEY);
        Clarity.identify(props.auth.data.uid);
      }
    }
  }, [props.auth.data.uid]);

  useLiveLocation({
    isAuth: auth.loggedIn,
    isLocationPermissionGranted: locationAccess === "granted",
    currentLocationFromStore: props.locations?.currentLocation
  });

  useEffect(() => {
    if (auth.loggedIn) {
      addUserDistance();
    }
    // eslint-disable-next-line
  }, [props.locations?.currentLocation, props.locations.selectedLocation]);

  const requestLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationAccess("granted");
      },
      (error) => {
        console.error("Location permission denied or failed", error);
        setLocationAccess("denied");
      }
    );
  };

  const handlePermissionChange = (setAccess) => (event) => {
    setAccess(event.target.state);
  };

  const queryPermission = (name, setAccess) => {
    if (navigator.permissions) {
      navigator.permissions.query({ name }).then((result) => {
        setAccess(result.state);
        const handleChange = handlePermissionChange(setAccess);
        result.addEventListener("change", handleChange);

        return () => {
          result.removeEventListener("change", handleChange);
        };
      });
    }
  };

  useEffect(() => {
    const cleanupLocation = queryPermission("geolocation", setLocationAccess);

    return () => {
      if (cleanupLocation) {
        cleanupLocation();
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleSWMessage = (event) => {
      const { type, data } = event.data || {};
      console.log(`Received SW message: ${type}`, data);

      switch (type) {
        case "RAISE_COMPLAINT_REQUESTS":
        case "RAISE_COMPLAINT_REQUESTS_UPDATED":
        case "QUEUED_REQUEST_ADDED":
          console.log("Updated face queue:", data);
          setOfflineComplaints(data || []);
          break;

        case "FORCE_UPDATE_AVAILABLE":
          console.log("Update available:", event.data?.build);
          break;

        default:
          break;
      }
    };

    const requestQueuedData = () => {
      if (navigator.serviceWorker?.controller) {
        console.log("Requesting queued face requests data");
        navigator.serviceWorker?.controller.postMessage(
          "GET_QUEUED_FACE_REQUESTS"
        );
      } else {
        console.log("No service worker controller available yet");
      }
    };

    // Add message event listener
    navigator.serviceWorker?.addEventListener("message", handleSWMessage);

    // Check if service worker is ready
    if (navigator.serviceWorker?.controller) {
      requestQueuedData();
    } else {
      // Wait for the service worker to be ready
      const onControllerChange = () => {
        console.log("Service worker controller change detected");
        requestQueuedData();
      };
      navigator.serviceWorker?.addEventListener(
        "controllerchange",
        onControllerChange
      );

      // Clean up
      return () => {
        navigator.serviceWorker?.removeEventListener(
          "controllerchange",
          onControllerChange
        );
      };
    }

    // Clean up the message event listener
    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
    };
  }, []);

  // First-ever load with nothing persisted yet (fresh device, cleared site
  // data, incognito): profile.data is null and the listener hasn't produced
  // a definitive answer. A user who already has profile data - the common
  // case, since it's persisted to localStorage independently of the
  // Firestore cache - skips this and keeps working, including offline.
  const isAwaitingFirstProfileSync =
    props.auth.data.uid !== null &&
    props.profile.data === null &&
    profileSyncState !== "ready";

  // The listener's own timeout only arms once auth resolves; this one covers
  // the gate even when it never does (token fetch failing, auth never firing).
  const [isGateTimedOut, setIsGateTimedOut] = useState(false);
  useEffect(() => {
    if (!isAwaitingFirstProfileSync) {
      setIsGateTimedOut(false);
      return;
    }
    const timer = setTimeout(
      () => setIsGateTimedOut(true),
      PROFILE_SYNC_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isAwaitingFirstProfileSync]);

  if (isAwaitingFirstProfileSync) {
    return (
      <ProfileSyncGate
        unreachable={profileSyncState === "unreachable" || isGateTimedOut}
        onRetry={() => window.location.reload()}
        onLogout={() => logout()}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <Login navigate={navigate} />
            </PublicRoute>
          }
        />

        <Route
          path="/complaints"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <Complaints
                navigate={navigate}
                profileOnClick={() => {
                  navigate("/settings");
                }}
                getNextSetOfComplaints={() => {
                  getComplaintsNextPage();
                }}
                paginationLoading={complaintsLoading.pagination}
                isIndependentEngineer={
                  auth.claims?.moduleAccess?.independentEngineer
                }
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <Profile navigate={navigate} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/linkComplaint"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <Complaints
                navigate={navigate}
                profileOnClick={() => {
                  navigate("/settings");
                }}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/raiseComplaint"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              {auth.claims?.moduleAccess?.support === true ? (
                <SupportRaiseComplaint
                  setFormData={setFormData}
                  formData={formData}
                  navigate={navigate}
                  profileOnClick={() => {
                    navigate("/settings");
                  }}
                />
              ) : (
                <></>
              )}

              {auth.claims?.moduleAccess?.independentEngineer === true ? (
                locationAccess !== "granted" && !window.Cypress ? (
                  <PermissionDeniedFallback
                    fallBackImage={<LocationFallBack />}
                    title={
                      locationAccess === "prompt"
                        ? "Allow Location Permission"
                        : "Location Permission Denied"
                    }
                    description={
                      locationAccess === "prompt"
                        ? "Enables us to monitor your real-time location"
                        : "You have blocked from tracking your location. To use this, change your location settings in browser."
                    }
                    showButton={locationAccess === "prompt"}
                    onEnableClick={() => {
                      requestLocationPermission();
                    }}
                    navigate={navigate}
                    profileOnClick={() => {
                      navigate("/settings");
                    }}
                  />
                ) : (
                  <IeRaiseComplaint
                    setFormData={setFormData}
                    formData={formData}
                    profileData={props.profile.data}
                    navigate={navigate}
                    profileOnClick={() => {
                      navigate("/settings");
                    }}
                    isAuth={auth.loggedIn}
                    requestLocationPermission={requestLocationPermission}
                  />
                )
              ) : (
                <></>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/raiseComplaint/success"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <SuccessFallback
                navigate={navigate}
                profileOnClick={() => {
                  navigate("/settings");
                }}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/locationSearch"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <LocationSearch
                navigate={navigate}
                locations={
                  isValidObject(props.locations.data)
                    ? Object.values(props.locations.data)
                    : []
                }
                loading={props.locations.loading}
                profileOnClick={() => {
                  navigate("/settings");
                }}
                setSelectedLocation={props.setSelectedLocation}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/complaints/:complaintId"
          element={
            <ProtectedRoute
              uid={props.auth.data.uid}
              profileData={props.profile.data}
            >
              <SelectedComplaint
                navigate={navigate}
                formData={formData}
                profileOnClick={() => {
                  navigate("/settings");
                }}
                isIndependentEngineer={
                  auth.claims?.moduleAccess?.independentEngineer
                }
                locationAccess={locationAccess}
                requestLocationPermission={requestLocationPermission}
                complaintsLoading={complaintsLoading}
                clearFormData={() => {
                  setFormData({
                    name: "",
                    issue: [],
                    locationData: {},
                    phoneNumber: "",
                    callId: ""
                  });
                }}
              />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/complaints" replace />} />
      </Routes>

      <SnackBar
        message={status}
        status={props.status}
        type={props.status.code === null ? "success" : "error"}
      />

      <div
        id="critical-update-card"
        className="display-none z-index-101 inherit-parent-height inherit-parent-width background-color-black-with-opacity-light position-absolute-center-self "
      >
        <footer className="position-absolute  inherit-parent-width bottom-0 padding-large">
          <div
            id="critical-update-card-contents-wrapper"
            className="background-white inherit-parent-width padding-large border-radius-default display-flex flex-direction-column flex-align-items-center "
          >
            <div className="margin-bottom-default">
              <UpdateReloadIcon />
            </div>
            <p className="font-family-gilroy-medium font-size-medium font-color-secondary text-align-center margin-bottom-default ">
              {" "}
              Critical update available{" "}
            </p>
            <p className="font-family-gilroy-regular font-size-medium font-color-secondary text-align-center margin-bottom-default ">
              New critical changes updated and ready to launch.
            </p>
            <button
              id="critical-update-confirm-button"
              className="inherit-parent-width padding-vertical-medium font-family-gilroy-bold border-radius-default font-size-medium cursor-pointer background-color-primary font-color-white box-shadow-none"
            >
              Okay
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  status: state.status,
  complaints: state.complaints,
  auth: state.auth,
  profile: state.profile,
  locations: state.locations
});

const mapDispatchToProps = () => ({
  logout: () => logout(),
  forceRefreshAccessToken: (status) => forceRefreshAccessToken(status),
  setUserLocation: (data) => setUserLocation(data),
  setSelectedLocation: (locationId) => setSelectedLocation(locationId),
  setFilter: (data) => setFilter(data)
});
export default connect(mapStateToProps, mapDispatchToProps)(App);

const ProtectedRoute = ({ uid, children, profileData }) => {
  if (uid === null && profileData === null) {
    return <Navigate to="/login" />;
  } else {
    return children;
  }
};

const PublicRoute = ({ uid, children, profileData }) => {
  if (uid === null && profileData === null) {
    return children;
  } else {
    return <Navigate to="/" />;
  }
};

// Shown only while an authenticated user's profile hasn't loaded yet - not
// on ordinary logout/login, which ProtectedRoute/PublicRoute already cover.
const ProfileSyncGate = ({ unreachable, onRetry, onLogout }) => {
  return (
    <div className="inherit-parent-height inherit-parent-width display-flex flex-direction-column flex-justify-content-center flex-align-items-center padding-large">
      <RotateIcon width="48" height="48" color="#C2C2C2" removeDot />
      <div className="padding-top-larger font-family-RHD-medium font-size-medium text-align-center">
        {unreachable ? "Could not load your profile" : "Connecting…"}
      </div>
      <div className="padding-top-medium font-size-medium font-color-secondary text-align-center">
        {unreachable
          ? "Check your connection and try again."
          : "Setting up your account. This should only take a moment."}
      </div>
      {unreachable && (
        <div className="inherit-parent-width padding-top-larger">
          <Button text="Retry" boxShadow={false} onClick={onRetry} />
          <Button
            className="margin-top-default"
            text="Log out and sign in again"
            variant="transparent"
            boxShadow={false}
            onClick={onLogout}
          />
        </div>
      )}
    </div>
  );
};
